import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Checkbox,
  Radio,
  Typography,
} from '@mui/material';
import { DataGridProps } from '@mui/x-data-grid';
import { stringifyPowerType } from '@nohara-dxdev/mt-common';
import { formatDateTime, PaginatedDataGrid } from '@nohara-dxdev/react-core';
import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  useController,
  FieldPath,
  FieldValues,
  RegisterOptions,
  useFormContext,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PowerTypeEntity } from '@/types/api';

import { usePowerTypes } from '../api/get-power-types';

interface SelectPowerTypeModalProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  name: TName;
  orgId: string;
  projectId: string;
  rules?: RegisterOptions<TFieldValues, TName>;
  open: boolean;
  onSelect?: (powerType: PowerTypeEntity | PowerTypeEntity[] | null) => void;
  onCancel?: () => void;
  onClose?: () => void;
  multiValue?: boolean;
  title?: string;
}

type SelectPowerTypeModalComponent = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: SelectPowerTypeModalProps<TFieldValues, TName>,
) => React.JSX.Element;

export const SelectPowerTypeModal: SelectPowerTypeModalComponent = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  orgId,
  projectId,
  rules,
  open,
  onSelect,
  onCancel,
  onClose,
  multiValue = false,
  title,
}: SelectPowerTypeModalProps<TFieldValues, TName>) => {
  const { t } = useTranslation();
  const form = useFormContext<TFieldValues>();

  // Form integration using react-hook-form
  const {
    field: { value, onChange },
    fieldState: { error: fieldError },
    formState: { disabled },
  } = useController({
    name,
    control: form.control,
    rules,
  });

  const [selectedPowerTypes, setSelectedPowerTypes] = useState<PowerTypeEntity[]>([]);

  useEffect(() => {
    if (!value) {
      setSelectedPowerTypes([]);
      return;
    }

    if (multiValue) {
      setSelectedPowerTypes(Array.isArray(value) ? value : [value]);
    } else {
      setSelectedPowerTypes(value ? [value] : []);
    }
  }, [value, multiValue]);

  // Load item templates with search filter and context filters
  const powerTypesQuery = usePowerTypes(orgId, projectId, {
    queryConfig: {
      enabled: open, // Only fetch when modal is open
    },
  });

  const { isLoading } = powerTypesQuery;

  // Handle template selection
  const handlePowerTypeSelect = useCallback(
    (powerType: PowerTypeEntity) => {
      if (multiValue) {
        setSelectedPowerTypes((prev) => {
          const isSelected = prev.some(
            (selectedPowerType) => selectedPowerType.id === powerType.id,
          );
          if (isSelected) {
            return prev.filter((selectedPowerType) => selectedPowerType.id !== powerType.id);
          } else {
            return [...prev, powerType];
          }
        });
      } else {
        setSelectedPowerTypes([powerType]);
      }
    },
    [multiValue],
  );

  // Handle select button click
  const handleSelect = useCallback(() => {
    onChange(multiValue ? selectedPowerTypes : selectedPowerTypes[0] || null);
    onSelect?.(multiValue ? selectedPowerTypes : selectedPowerTypes[0] || null);
    onClose?.();
  }, [selectedPowerTypes, multiValue, onChange, onSelect, onClose]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    // Reset selection to current form value
    if (!value) {
      setSelectedPowerTypes([]);
    } else {
      // Handle both single value and multi-value cases
      if (multiValue) {
        setSelectedPowerTypes(Array.isArray(value) ? value : [value]);
      } else {
        setSelectedPowerTypes([value]);
      }
    }
    onCancel?.();
    onClose?.();
  }, [value, multiValue, onCancel, onClose]);

  // Define table columns
  const columns: DataGridProps<PowerTypeEntity>['columns'] = useMemo(
    () => [
      {
        field: 'selection',
        headerName: '',
        width: 80,
        sortable: false,
        renderCell: (params) => {
          const isSelected = selectedPowerTypes.some((powerType) => powerType.id === params.row.id);

          if (multiValue) {
            return (
              <Checkbox
                checked={isSelected}
                onChange={() => {
                  handlePowerTypeSelect(params.row);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                }}
                disabled={disabled}
              />
            );
          } else {
            return (
              <Radio
                checked={isSelected}
                onChange={() => {
                  handlePowerTypeSelect(params.row);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                }}
                disabled={disabled}
              />
            );
          }
        },
      },
      {
        field: 'type',
        headerName: t('powerTypes.list.columns.type'),
        flex: 1,
        valueGetter: (_value, row) => stringifyPowerType(row),
      },
      {
        field: 'createdAt',
        headerName: t('common.table.createdAt'),
        flex: 1,
        valueFormatter: (value: number) => formatDateTime(value),
      },
    ],
    [selectedPowerTypes, multiValue, handlePowerTypeSelect, disabled, t],
  );

  const modalTitle =
    title ??
    (multiValue
      ? t('common.selectModal.titleMultiple', { type: t('powerTypes.title') })
      : t('common.selectModal.titleSingle', { type: t('powerTypes.title') }));

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="lg"
      fullWidth
      slotProps={{
        paper: { sx: { height: '80vh', maxHeight: 800 } },
      }}
    >
      <DialogTitle>
        <Typography variant="h6">{modalTitle}</Typography>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 2 }}>
        {/* Power Types Table */}
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <PaginatedDataGrid
            paginatedQuery={powerTypesQuery}
            columns={columns}
            loading={isLoading}
            getRowId={(row) => row.id}
            hideFooterSelectedRowCount
            pagination
            sx={{
              '& .MuiDataGrid-row': {
                cursor: 'pointer',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'action.hover',
              },
            }}
            onRowClick={(params) => {
              handlePowerTypeSelect(params.row);
            }}
            slotProps={{
              noRowsOverlay: {
                title: t('common.selectModal.noItems', { type: t('powerTypes.title') }),
              },
            }}
          />
        </Box>

        {/* Selection Summary */}
        {selectedPowerTypes.length > 0 && (
          <Box sx={{ py: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {multiValue
                ? t('common.selectModal.selectedMultiple', {
                    type: t('powerTypes.title'),
                    count: selectedPowerTypes.length,
                  })
                : t('common.selectModal.selectedSingle', {
                    type: t('powerTypes.title'),
                    name: stringifyPowerType(selectedPowerTypes[0]) || '',
                  })}
            </Typography>
          </Box>
        )}

        {/* Error Display */}
        {fieldError && (
          <Typography variant="body2" color="error">
            {fieldError.message}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleCancel} disabled={disabled}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSelect}
          variant="contained"
          disabled={disabled || selectedPowerTypes.length === 0}
        >
          {t('common.select')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
